import { useState, useCallback, useMemo, useEffect } from "react"
import { uniq } from "lodash"

import { Guid, IAppliedFilter, IMultipleEntities } from "@/types"
import { AssetStatus } from "models/Asset"
import { collectFilterProps } from "utils/filters"
import usePagedData from "./usePagedData"
import AssetArticle from "models/AssetArticle"
import ArticleService from "services/ArticleService"
import toastMessages from "stores/toastMessages"
import ViewSettingsInventory from "models/ViewSettingsInventory"
import AssetDisplayStatus from "models/AssetDisplayStatus"
import AssetService, { IArticleAvailability, ICheckArticlesAvailabilityParams, ICheckAssetsAvailabilityParams } from "services/AssetService"
import ArticleAsset from "models/ArticleAsset"

export interface IUseAssetsParams {
    key?: string
    filters: IAppliedFilter[] | null
    articleId?: string
    viewSettings?: ViewSettingsInventory
    pageSize?: number
    withDisplayStatuses?: boolean
    withArticleLevels?: boolean
    assetAvailabilityParams?: Omit<ICheckAssetsAvailabilityParams, "assetIds">
    articleAvailabilityParams?: Omit<ICheckArticlesAvailabilityParams, "articleIds">
    descriptionAsc?: boolean
    onFetched?: (data: IMultipleEntities<AssetArticle>, isFirstPage?: boolean) => void | Promise<void>
}

interface IArticleLevelsArticle {
    articleId: Guid
    articleDescription: string
}

type ArticlesForArticleLevels = Record<string, ArticleAsset>
export type ArticleLevelsCache = Record<Guid, { highLevelArticle: IArticleLevelsArticle | null, lowLevelArticles: IArticleLevelsArticle[] }>

export default function useAssets({
    key,
    filters,
    articleId,
    viewSettings,
    pageSize = 30,
    withDisplayStatuses,
    withArticleLevels,
    assetAvailabilityParams,
    articleAvailabilityParams,
    descriptionAsc,
    onFetched,
}: IUseAssetsParams) {
    const [displayStatuses, setDisplayStatuses] = useState<Record<Guid, AssetDisplayStatus>>({})
    const [assetAvailabilities, setAssetAvailabilities] = useState<Record<Guid, boolean>>({})
    const [articleAvailabilities, setArticleAvailabilities] = useState<Record<Guid, IArticleAvailability>>({})
    const [articlesForArticleLevels, setArticlesForArticleLevels] = useState<ArticlesForArticleLevels>({})

    const assetFilterProps = useMemo(() => {
        if (!filters) return
        const filterProps = collectFilterProps(filters)

        if (!viewSettings?.showInactiveAssets) {
            if (!filterProps.statuses) {
                filterProps.statuses = [AssetStatus.ordered, AssetStatus.confirmed, AssetStatus.available, AssetStatus.active]
            } else if (filterProps.statuses.length === 1 && filterProps.statuses[0] === AssetStatus.inactive) {
                filterProps.statuses = []
            } else {
                filterProps.statuses = filterProps.statuses.filter((status: AssetStatus) => status !== AssetStatus.inactive)
            }
        }

        if (assetAvailabilityParams) {
            assetAvailabilityParams.statuses = filterProps.statuses
        }
        if (articleAvailabilityParams) {
            articleAvailabilityParams.statuses = filterProps.statuses
        }

        return filterProps
        // NOTE: We intentionally exclude availability params objects here because
        // they are re-applied via a separate effect to avoid wiping the asset list
        // (which would reset scroll position).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, viewSettings?.showInactiveAssets])

    const assetsParams = useMemo(() => {
        if (!assetFilterProps) return
        return {
            ...assetFilterProps,
            ...articleId ? { articleIds: [articleId] } : {},
            ...typeof descriptionAsc === "boolean" ? { descriptionAsc } : {},
            pageSize,
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key, articleId, assetFilterProps, descriptionAsc, pageSize])

    // Fetching the base asset/article list should be independent from date-based
    // availability params so that shifting/expanding the scheduler period does NOT
    // clear & re-fetch the entire paged list (which causes scroll jump to top).
    // Therefore we intentionally exclude availability params from dependencies.
    const assetsCall = useCallback(() => {
        return ArticleService.getAssetArticles(assetsParams!)
    }, [assetsParams])

    const fetchAssetDisplayStatuses = useCallback(async (assetIds: Guid[], isFirstPage?: boolean) => {
        if (!assetIds.length) return
        const newDisplayStatuses = await AssetService.getAssetDisplayStatuses(assetIds)
        setDisplayStatuses(current => ({
            ...isFirstPage ? {} : current,
            ...newDisplayStatuses
        }))
    }, [])

    const fetchAssetAvailabilities = useCallback(async (assetIds: Guid[], isFirstPage?: boolean) => {
        if (!assetAvailabilityParams) return
        const availableAssetIds = await AssetService.checkAssetsAvailability({
            ...assetAvailabilityParams,
            assetIds,
        })
        setAssetAvailabilities(current => ({
            ...isFirstPage ? {} : current,
            ...assetIds.reduce((acc, id) => ({
                ...acc,
                [id]: availableAssetIds.includes(id)
            }), {})
        }))

    }, [assetAvailabilityParams])

    const fetchArticleAvailabilities = useCallback(async (articleIds: Guid[], isFirstPage?: boolean) => {
        if (!articleAvailabilityParams) return
        const newArticleAvailabilities = await AssetService.checkArticlesAvailability({
            ...articleAvailabilityParams,
            articleIds,
        })
        setArticleAvailabilities(current => ({
            ...isFirstPage ? {} : current,
            ...newArticleAvailabilities.reduce((acc, articleAvailability) => ({
                ...acc,
                [articleAvailability.id]: articleAvailability
            }), {})
        }))
    }, [articleAvailabilityParams])

    const fetchArticleLevels = useCallback(async (assetArticles: AssetArticle[]) => {

        const articleNumbersWithoutLevelsFetched: string[] = []

        for (const aa of assetArticles) {
            if (aa.highLevelArticleNumber && !articlesForArticleLevels[aa.highLevelArticleNumber]) {
                articleNumbersWithoutLevelsFetched.push(aa.highLevelArticleNumber)
            }
            if (!aa.lowLevelArticleNumbers.length) continue
            for (const lowLevelArticleNumber of aa.lowLevelArticleNumbers) {
                if (!articlesForArticleLevels[lowLevelArticleNumber]) {
                    articleNumbersWithoutLevelsFetched.push(lowLevelArticleNumber)
                }
            }
        }

        const uniqArticleNumbersWithoutLevelsFetched = uniq(articleNumbersWithoutLevelsFetched)

        if (!uniqArticleNumbersWithoutLevelsFetched.length) return

        const articleAssets = await ArticleService.getArticleAssets({
            articleNumbers: uniqArticleNumbersWithoutLevelsFetched,
            pageSize: uniqArticleNumbersWithoutLevelsFetched.length,
            isOnlyForHolder: false,
        })

        if (!articleAssets || articleAssets.list.length === 0) return

        setArticlesForArticleLevels({
            ...articlesForArticleLevels,
            ...articleAssets.list.reduce((acc, articleAsset) => ({
                ...acc,
                [articleAsset.articleNo]: articleAsset
            }), {} as ArticlesForArticleLevels)
        })
    }, [articlesForArticleLevels])

    const onAssetsFetched = useCallback(async (data: IMultipleEntities<AssetArticle>, isFirstPage?: boolean) => {
        const assetIds = data.list.map(a => a.assetId)
        const promises: (Promise<void> | void)[] = []

        if (withDisplayStatuses) {
            promises.push(fetchAssetDisplayStatuses(assetIds, isFirstPage))
        }

        if (assetAvailabilityParams) {
            promises.push(fetchAssetAvailabilities(assetIds, isFirstPage))
        }

        if (articleAvailabilityParams) {
            const articleIds = new Set<Guid>()
            data.list.forEach(a => articleIds.add(a.articleId))
            promises.push(fetchArticleAvailabilities(Array.from(articleIds), isFirstPage))
        }

        if (withArticleLevels) {
            promises.push(fetchArticleLevels(data.list))
        }

        if (onFetched) {
            promises.push(onFetched(data, isFirstPage))
        }

        await Promise.allSettled(promises)
    }, [articleAvailabilityParams, assetAvailabilityParams, fetchArticleAvailabilities, fetchArticleLevels,
        fetchAssetAvailabilities, fetchAssetDisplayStatuses, onFetched, withArticleLevels, withDisplayStatuses])

    // When availability params change (usually due to date range shift/extend), refresh
    // only availability related state for the CURRENT in-memory assets instead of
    // triggering a full list refetch (which would clear items & reset scroll position).
    useEffect(() => {
        if (!assets || (!assetAvailabilityParams && !articleAvailabilityParams)) return
        const assetIds = assets.map(a => a.assetId)
        const articleIds = Array.from(new Set(assets.map(a => a.articleId)))
        const promises: (Promise<void> | void)[] = []
        if (assetAvailabilityParams) promises.push(fetchAssetAvailabilities(assetIds, true))
        if (articleAvailabilityParams) promises.push(fetchArticleAvailabilities(articleIds, true))
        Promise.allSettled(promises)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assetAvailabilityParams, articleAvailabilityParams])

    const {
        items: assets,
        loadNextPage: loadMoreAssets,
        isInitialLoad: isInitialLoadAssets,
        isLoading: isLoadingAssets,
        hasMore: hasMoreAssets,
        pagination: assetsPagination,
    } = usePagedData<AssetArticle>(
        assetsParams ? assetsCall : undefined,
        toastMessages.showSystemError,
        onAssetsFetched,
    )

    const articleLevels: ArticleLevelsCache = useMemo(() => {
        if (!withArticleLevels || !assets) return {}
        return assets.reduce((acc, asset) => {
            return {
                ...acc,
                [asset.assetId]: {
                    highLevelArticle: asset.highLevelArticleNumber && articlesForArticleLevels[asset.highLevelArticleNumber] ? {
                        articleId: articlesForArticleLevels[asset.highLevelArticleNumber].id,
                        articleDescription: articlesForArticleLevels[asset.highLevelArticleNumber].description,
                    } : null,
                    lowLevelArticles: asset.lowLevelArticleNumbers
                        .filter(articleNumber => articlesForArticleLevels[articleNumber])
                        .map(articleNumber => ({
                            articleId: articlesForArticleLevels[articleNumber].id,
                            articleDescription: articlesForArticleLevels[articleNumber].description,
                        }))
                }
            }
        }, {} as ArticleLevelsCache)
    }, [articlesForArticleLevels, assets, withArticleLevels])

    return {
        assets,
        loadMoreAssets,
        isInitialLoadAssets,
        isLoadingAssets,
        hasMoreAssets,
        assetsPagination,
        displayStatuses,
        assetAvailabilities,
        articleAvailabilities,
        articleLevels,
    }
}
